import { useState } from 'react'
import {
  Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts'
import { TrendingUp } from 'lucide-react'
import { ChartCard } from '@/components/common'
import { Skeleton } from '@/components/ui/skeleton'
import { ErrorState } from '@/components/common/ErrorState'
import { PeriodSelect } from './PeriodSelect'
import { useEmployeeTrend } from '../api'

const PERIODS = [
  { value: '6', label: 'Last 6 Months' },
  { value: '12', label: 'Last 12 Months' },
]

/** Headcount at each month end, as a line over a soft gradient fill. */
export function EmployeeTrendCard() {
  const [months, setMonths] = useState<'6' | '12'>('6')
  const { data, isLoading, error, refetch } = useEmployeeTrend(Number(months) as 6 | 12)

  // A fixed 0-based axis would flatten a curve that moves by 10% of its value, so the
  // domain is padded around the actual range instead.
  const counts = data?.points.map((point) => point.headcount) ?? []
  const min = counts.length ? Math.min(...counts) : 0
  const max = counts.length ? Math.max(...counts) : 100
  const padding = Math.max(Math.round((max - min) * 0.35), 20)

  return (
    <ChartCard
      title="Employee Trend"
      icon={TrendingUp}
      filter={
        <PeriodSelect
          value={months}
          options={PERIODS}
          onChange={(value) => setMonths(value as '6' | '12')}
          label="Trend period"
        />
      }
      footerLink={{ label: 'View Full Report', to: '/reports' }}
    >
      {isLoading && <Skeleton className="my-4 h-[190px] w-full" />}
      {error != null && <ErrorState error={error} onRetry={() => void refetch()} />}

      {data && (
        <div style={{ height: 200 }} className="w-full py-2">
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={data.points} margin={{ top: 8, right: 8, bottom: 0, left: -12 }}>
              <defs>
                <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--color-primary)" stopOpacity={0.22} />
                  <stop offset="100%" stopColor="var(--color-primary)" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="var(--color-border)" vertical={false} />
              <XAxis
                dataKey="label"
                tickLine={false}
                axisLine={false}
                tick={{ fill: 'var(--color-muted)', fontSize: 11 }}
                dy={6}
              />
              <YAxis
                domain={[Math.max(min - padding, 0), max + padding]}
                tickLine={false}
                axisLine={false}
                tick={{ fill: 'var(--color-muted)', fontSize: 11 }}
                width={52}
                tickFormatter={(value: number) => new Intl.NumberFormat('en').format(value)}
              />
              <Tooltip
                cursor={{ stroke: 'var(--color-border)' }}
                contentStyle={{
                  borderRadius: 'var(--radius-control)',
                  border: '1px solid var(--color-border)',
                  background: 'var(--color-card)',
                  fontSize: 12,
                }}
                // Recharts types the value as possibly undefined, so guard rather than cast.
                formatter={(value) => [
                  typeof value === 'number' ? new Intl.NumberFormat('en').format(value) : String(value ?? '—'),
                  'Headcount',
                ]}
              />
              <Area
                type="monotone"
                dataKey="headcount"
                stroke="var(--color-primary)"
                strokeWidth={2}
                fill="url(#trendFill)"
                dot={{ r: 3, fill: 'var(--color-card)', stroke: 'var(--color-primary)', strokeWidth: 2 }}
                activeDot={{ r: 5 }}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </ChartCard>
  )
}
